'use client';

import Link from 'next/link';
import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '#/lib/utils';
import { MenuItem } from '#/data/menu';
import { trackEvent } from '#/lib/analytics';

interface TryMenuProps {
  route: MenuItem;
  pathname: string;
}

/**
 * 헤더의 대표 진입점. 링크 본체는 곧장 맞춤법 검사로 보내고, 나머지 도구는
 * 옆 화살표(호버·클릭·키보드 포커스)로 펼친다. /qa 같은 깊은 링크로 들어온
 * 방문자가 로고 아이콘 말고는 기능을 찾을 길이 없던 문제를 메운다.
 */
export function TryMenu({ route, pathname }: TryMenuProps) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  // 트리거와 패널 사이 여백을 지날 때 메뉴가 깜빡이지 않도록 닫기만 조금 늦춘다.
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => cancelClose, []);
  useEffect(() => setOpen(false), [pathname]);

  const track = (to: string, label: string) =>
    trackEvent('click_other_menu', {
      location: 'header_nav',
      from: pathname,
      to,
      label
    });

  const children = route.children ?? [];

  return (
    <div
      className="relative"
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
      onFocus={() => {
        cancelClose();
        setOpen(true);
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setOpen(false);
        }
      }}
    >
      <div
        className={cn(
          'flex items-center rounded-full pl-3 pr-1.5 py-1.5 transition-colors',
          'bg-[#104573] text-white hover:bg-[#0b3357]',
          'dark:bg-[#60a3d9] dark:text-[#0c1a2e] dark:hover:bg-[#8dbfe6]'
        )}
      >
        <Link
          href={route.href}
          onClick={() => track(route.href, route.label)}
          className="text-sm font-semibold"
        >
          {route.label}
        </Link>
        <button
          type="button"
          aria-expanded={open}
          aria-label="사용해 볼 기능 더 보기"
          onClick={() => setOpen((v) => !v)}
          className="ml-0.5 rounded-full p-1"
        >
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 transition-transform duration-200',
              open && 'rotate-180'
            )}
          />
        </button>
      </div>

      {open && (
        <div
          className={cn(
            'absolute left-0 top-full z-50 mt-2 min-w-[14rem] rounded-lg p-1 shadow-lg',
            'border border-[#104573]/10 bg-white',
            'dark:border-white/10 dark:bg-[#0c1a2e]'
          )}
        >
          {children.map((child) => (
            <Link
              key={child.href}
              href={child.href}
              onClick={() => {
                track(child.href, child.label);
                setOpen(false);
              }}
              className={cn(
                'block rounded-md px-3 py-2 text-sm transition-colors',
                child.active
                  ? 'bg-[#104573]/10 font-medium text-[#104573] dark:bg-[#60a3d9]/15 dark:text-[#60a3d9]'
                  : 'text-muted-foreground hover:bg-[#104573]/5 hover:text-[#104573] dark:hover:text-[#60a3d9]'
              )}
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
